/**
 * Lobby configuration diagnostics — developer-facing console output only.
 *
 * Misconfigured hosts / flags / sections make parts of the lobby fail silently
 * (e.g. an empty prize pop host disables the campaigns query with no log).
 * This module inspects the effective configuration and logs one actionable
 * `[Lobby config]` message per problem. It never changes lobby behavior.
 *
 * Message format: line 1 is the problem, followed (when they exist) by an
 * "  → Impact: " line with the consequence and an "  → Expected: " line with
 * the expected configuration.
 *
 * Two phases:
 *  1. Startup — once the (delayed) SSI check has settled: core hosts, SSI
 *     presence, campaign hosts, campaign-only host.
 *  2. Settings landed — when `settingsRequestDone` flips to true, sections and
 *     feature flags are known: section/flag/host consistency per campaign, the
 *     campaign-only view, and (after a grace period) sessionKey / playerId.
 *
 * Every message is logged at most once.
 */

import {
  campaignConfigRegistry,
  PRIZE_POP_CAMPAIGN_TYPE,
  REBATE_CAMPAIGN_TYPE,
  TOURNAMENTS_CAMPAIGN_TYPE,
  type CampaignTypes,
  type ICampaignConfig,
} from 'features/campaigns';
import { useAppStore } from '../../store/app/appStore';
import { useFeatureFlagsStore, type Flags } from '../../store/featureFlags/featureFlagsStore';
import { isLocalEnvironment } from '../../core/utils/environment';
import { EGT } from '../../constants';
import type { ISettings } from '../../types/models/ISettings';

const LOG_PREFIX = '[Lobby config]';
const IMPACT_PREFIX = '  → Impact: ';
const EXPECTED_PREFIX = '  → Expected: ';

// sessionKey / playerId may legitimately arrive later via the SETTINGS postMessage.
const SESSION_GRACE_PERIOD_MS = 5000;

// Hosts that look like QA backends. Only flagged in RELEASE builds.
const QA_HOST_PATTERN = /[-.]qa[-.]/i;

const CAMPAIGN_FEATURE_FLAG: Record<CampaignTypes, keyof Flags> = {
  [PRIZE_POP_CAMPAIGN_TYPE]: 'enablePrizePopCampaign',
  [TOURNAMENTS_CAMPAIGN_TYPE]: 'enableTournamentsCampaign',
  [REBATE_CAMPAIGN_TYPE]: 'enableRebateCampaign',
};

type CoreHostKey = 'apiHost' | 'assetsHost' | 'redirectHost' | 'host';

interface ICoreHostCheck {
  settingsKey: CoreHostKey;
  label: string;
  ssiKey: string;
  envVarKey: string;
  level: 'error' | 'warn';
  impact: string;
}

const CORE_HOST_CHECKS: ICoreHostCheck[] = [
  {
    settingsKey: 'apiHost',
    label: 'API host',
    ssiKey: 'apiIntegrationHost',
    envVarKey: 'API_INTEGRATION_HOST',
    level: 'error',
    impact: 'Lobby settings (sections, games, feature flags) will not be fetched — the lobby stays empty.',
  },
  {
    settingsKey: 'assetsHost',
    label: 'Assets host',
    ssiKey: 'assetsHost',
    envVarKey: 'ASSETS_HOST',
    level: 'error',
    impact: 'Fonts, images and translations cannot be loaded.',
  },
  {
    settingsKey: 'redirectHost',
    label: 'Redirect host',
    ssiKey: 'redirectHost',
    envVarKey: 'LOBBY_REDIRECT_HOST',
    level: 'error',
    impact: 'Game launches have no redirect target.',
  },
  {
    settingsKey: 'host',
    label: 'Lobby host',
    ssiKey: 'lobbyHost',
    envVarKey: 'LOBBY_HOST',
    level: 'warn',
    impact: 'Help content cannot be loaded.',
  },
];

const reported = new Set<string>();
let started = false;
let sessionCheckTimer: ReturnType<typeof setTimeout> | undefined;

// All warnings: no background — keeps the console's default warning row, only the text is recolored.
const WARN_LOG_STYLE = 'color: #c57418; font-weight: bold;';

// Errors get a muted badge per category — campaign (violet) vs core config (warm brown) — so they're easy to tell apart.
const CAMPAIGN_ERROR_LOG_STYLE = 'color: #d9d2e6; background: #463a5c; padding: 2px 6px; border-radius: 3px;';
const CORE_ERROR_LOG_STYLE = 'color: #ddd1c3; background: #4a3b2f; padding: 2px 6px; border-radius: 3px;';

// Red exclamation mark + siren in front of every error, core and campaign alike.
const ERROR_MARK = '❗🚨';

/** Logs `problem` on line 1, then `impact` and `expected` on their own labelled lines when given. */
const report = (
  level: 'error' | 'warn',
  problem: string,
  impact?: string,
  expected?: string,
  isCampaign = false,
): void => {
  const prefix = level === 'error' ? `${ERROR_MARK} ${LOG_PREFIX}` : LOG_PREFIX;
  const lines = [`${prefix} ${problem}`];
  if (impact) lines.push(`${IMPACT_PREFIX}${impact}`);
  if (expected) lines.push(`${EXPECTED_PREFIX}${expected}`);
  const message = lines.join('\n');
  if (reported.has(message)) return;
  reported.add(message);
  const style = level === 'warn' ? WARN_LOG_STYLE : isCampaign ? CAMPAIGN_ERROR_LOG_STYLE : CORE_ERROR_LOG_STYLE;
  // The message goes in as a %s argument, never as the format string, so a '%' inside a host value can't be misread.
  const args = ['%c%s', style, message];
  switch (level) {
    case 'error':
      console.error(...args);
      break;
    case 'warn':
      console.warn(...args);
      break;
  }
};

/** Same as `report`, styled as a campaign message. */
const reportCampaign = (level: 'error' | 'warn', problem: string, impact?: string, expected?: string): void =>
  report(level, problem, impact, expected, true);

const readString = (source: object | undefined, key: string | undefined): string => {
  if (!source || !key) return '';
  const value: unknown = Reflect.get(source, key);
  return typeof value === 'string' ? value.trim() : '';
};

const getSsi = (): object | undefined => {
  const ssi = window.lobbySsi;
  return ssi !== undefined && typeof ssi === 'object' ? ssi : undefined;
};

/** Where a missing host is expected to come from, depending on whether SSI is in play. */
const expectedHostSource = (ssiKey: string | undefined, optionKey: string, envVarKey: string | undefined): string => {
  if (getSsi() && ssiKey) {
    return `"${ssiKey}" set in config/lobby_config.js (window.lobbySsi)`;
  }
  const envHint = envVarKey ? ` or ${envVarKey} at build time` : '';
  return `"${optionKey}" launch option${envHint} (window.lobbySsi is not present)`;
};

const expectedFlagOn = (flagKey: keyof Flags): string =>
  `feature flag "${flagKey}" enabled in the lobby settings response (featureFlags)`;

const expectedSection = (sectionName: string): string =>
  `"${sectionName}" section configured in the lobby settings response`;

const isDemo = (): boolean => useAppStore.getState().balance?.currency?.toUpperCase() === EGT;

const isCampaignFlagOn = (type: CampaignTypes): boolean =>
  useFeatureFlagsStore.getState().flags[CAMPAIGN_FEATURE_FLAG[type]] === true;

const getCampaignHost = (config: ICampaignConfig, settings: ISettings): string =>
  readString(settings, config.hostSettingsKey);

// ---------------------------------------------------------------------------
// Phase 1 — startup
// ---------------------------------------------------------------------------

const checkSsiPresence = (): void => {
  if (getSsi() || isLocalEnvironment()) return;
  report(
    'warn',
    'window.lobbySsi is not defined (config/lobby_config.js was not included via SSI).',
    'Hosts fall back to launch options and build-time env values (empty in production builds).',
    'config/lobby_config.js included via SSI, defining window.lobbySsi',
  );
};

const checkCoreHosts = (): void => {
  const { settings } = useAppStore.getState();

  CORE_HOST_CHECKS.forEach(({ settingsKey, label, ssiKey, envVarKey, level, impact }) => {
    if (settings[settingsKey]) return;
    report(level, `${label} is empty.`, impact, expectedHostSource(ssiKey, settingsKey, envVarKey));
  });

  const currency = useAppStore.getState().balance?.currency;
  if (!currency) {
    report('error', 'Currency is empty.', 'Lobby settings will not be fetched.', '"currency" launch option set');
  }
};

const checkCampaignHosts = (rawOptions: Partial<ISettings>): void => {
  const { settings } = useAppStore.getState();
  const ssi = getSsi();

  Object.values(campaignConfigRegistry).forEach((config) => {
    const name = config.displayName;
    const host = getCampaignHost(config, settings);
    const ssiValue = readString(ssi, config.ssiKey);
    const optionValue = readString(rawOptions, config.hostSettingsKey);

    if (!host) {
      if (ssi && !ssiValue && optionValue && config.ssiKey) {
        reportCampaign(
          'warn',
          `${name}: the "${config.hostSettingsKey}" launch option ("${optionValue}") is ignored because ` +
            `window.lobbySsi is present and takes precedence, but has no "${config.ssiKey}".`,
          `${name} campaigns will not be fetched.`,
          `"${config.ssiKey}" set in config/lobby_config.js (window.lobbySsi)`,
        );
        return;
      }
      reportCampaign(
        'warn',
        `${name}: host is empty.`,
        `${name} campaigns will not be fetched.`,
        expectedHostSource(config.ssiKey, config.hostSettingsKey, config.envVarKey),
      );
      return;
    }

    if (ssi && ssiValue && config.ssiKey && host !== ssiValue) {
      reportCampaign(
        'warn',
        `${name}: window.lobbySsi.${config.ssiKey} is "${ssiValue}" but the lobby is using "${host}".`,
        `${name} requests go to "${host}" — the SSI value was not applied.`,
        `lobby "${config.hostSettingsKey}" equal to window.lobbySsi.${config.ssiKey} ("${ssiValue}")`,
      );
    }
  });
};

const checkQaHostsInRelease = (): void => {
  if (process.env['DEPLOY_ENV'] !== 'RELEASE') return;
  const { settings } = useAppStore.getState();

  const hosts: Array<[string, string, boolean]> = [
    ...CORE_HOST_CHECKS.map(({ label, settingsKey }): [string, string, boolean] => [
      label,
      settings[settingsKey],
      false,
    ]),
    ...Object.values(campaignConfigRegistry).map((config): [string, string, boolean] => [
      `${config.displayName} host`,
      getCampaignHost(config, settings),
      true,
    ]),
  ];

  hosts.forEach(([label, host, isCampaign]) => {
    if (host && QA_HOST_PATTERN.test(host)) {
      (isCampaign ? reportCampaign : report)(
        'warn',
        `${label} points to a QA backend ("${host}") in a RELEASE build.`,
        'Requests from this RELEASE build go to a QA environment.',
        `a production ${label} in config/lobby_config.js (window.lobbySsi)`,
      );
    }
  });
};

// ---------------------------------------------------------------------------
// Campaign-only view (in-game cup / banner)
// ---------------------------------------------------------------------------

const checkCampaignOnly = (): void => {
  const { settings, sections, settingsRequestDone } = useAppStore.getState();
  const type = settings.campaignOnly?.type;
  if (!type) return;

  const config: ICampaignConfig | undefined = campaignConfigRegistry[type];
  if (!config) {
    reportCampaign(
      'error',
      `Campaign-only mode requested unknown campaign type "${type}".`,
      'The campaign view cannot render.',
      `campaignOnly.type one of: ${Object.keys(campaignConfigRegistry).join(', ')}`,
    );
    return;
  }

  const name = config.displayName;
  if (!getCampaignHost(config, settings)) {
    reportCampaign(
      'error',
      `${name}: the lobby was opened in campaign-only mode for "${type}" but the ${name} host is empty.`,
      'The campaign view will stay empty.',
      expectedHostSource(config.ssiKey, config.hostSettingsKey, config.envVarKey),
    );
  }

  // Flags and sections are only meaningful once the lobby settings response has landed.
  if (!settingsRequestDone || sections.length === 0) return;

  if (!isCampaignFlagOn(type)) {
    reportCampaign(
      'error',
      `${name}: the lobby was opened in campaign-only mode for "${type}" but feature flag ` +
        `"${CAMPAIGN_FEATURE_FLAG[type]}" is off.`,
      'The campaign view will not render.',
      expectedFlagOn(CAMPAIGN_FEATURE_FLAG[type]),
    );
  }

  if (type === PRIZE_POP_CAMPAIGN_TYPE && !sections.some((s) => s.name === config.sectionName)) {
    reportCampaign(
      'error',
      `${name}: the lobby was opened in campaign-only mode for "${type}" but the lobby settings have no ` +
        `"${config.sectionName}" section.`,
      `${name} campaigns are never fetched without it (empty view + loader).`,
      expectedSection(config.sectionName),
    );
  }
};

// ---------------------------------------------------------------------------
// Phase 2 — lobby settings response landed (sections + feature flags known)
// ---------------------------------------------------------------------------

/** Campaign types that the lobby will actually try to show. */
const getCampaignsInUse = (): ICampaignConfig[] => {
  const { settings, sections } = useAppStore.getState();
  const campaignOnlyType = settings.campaignOnly?.type;

  return Object.values(campaignConfigRegistry).filter(
    (config) =>
      Boolean(getCampaignHost(config, settings)) &&
      (config.type === campaignOnlyType ||
        (isCampaignFlagOn(config.type) && sections.some((s) => s.name === config.sectionName))),
  );
};

const checkCampaignSections = (): void => {
  const { settings, sections } = useAppStore.getState();
  // A failed settings request also marks settingsRequestDone — nothing to compare against.
  if (sections.length === 0) return;

  Object.values(campaignConfigRegistry).forEach((config) => {
    const name = config.displayName;
    const host = getCampaignHost(config, settings);
    const flagOn = isCampaignFlagOn(config.type);
    const hasSection = sections.some((s) => s.name === config.sectionName);

    if (hasSection && !host) {
      reportCampaign(
        'error',
        `${name}: the lobby settings contain the "${config.sectionName}" section but the ${name} host is empty.`,
        'The section will stay empty.',
        expectedHostSource(config.ssiKey, config.hostSettingsKey, config.envVarKey),
      );
    }

    if (hasSection && !flagOn) {
      reportCampaign(
        'warn',
        `${name}: the lobby settings contain the "${config.sectionName}" section but feature flag ` +
          `"${CAMPAIGN_FEATURE_FLAG[config.type]}" is off.`,
        'The section is hidden.',
        expectedFlagOn(CAMPAIGN_FEATURE_FLAG[config.type]),
      );
    }

    // Prize pop is the only campaign whose query is gated on its section existing.
    if (config.type === PRIZE_POP_CAMPAIGN_TYPE && host && flagOn && !hasSection) {
      reportCampaign(
        'warn',
        `${name}: host is configured but the lobby settings have no "${config.sectionName}" section.`,
        `${name} campaigns are only fetched when that section exists.`,
        expectedSection(config.sectionName),
      );
    }
  });
};

const checkSession = (): void => {
  if (isDemo()) return;
  const inUse = getCampaignsInUse();
  if (inUse.length === 0) return;

  const { settings } = useAppStore.getState();
  const names = inUse.map((config) => config.displayName).join(', ');

  if (!settings.sessionKey) {
    reportCampaign(
      'error',
      `Campaigns (${names}): "sessionKey" is still empty.`,
      'Campaign requests stay disabled until it is set.',
      '"sessionKey" passed as a launch option or in the SETTINGS message',
    );
  }
  if (!settings.playerId) {
    reportCampaign(
      'error',
      `Campaigns (${names}): "playerId" is still empty.`,
      'Campaign requests stay disabled until it is set.',
      '"playerId" passed as a launch option or in the SETTINGS message',
    );
  }
};

const runSettingsLandedChecks = (): void => {
  checkCampaignSections();
  checkCampaignOnly();

  if (sessionCheckTimer !== undefined) clearTimeout(sessionCheckTimer);
  sessionCheckTimer = setTimeout(checkSession, SESSION_GRACE_PERIOD_MS);
};

/**
 * Runs the startup checks and subscribes to the app store for the checks that
 * need the lobby settings response. Call once, after SSI has been applied to
 * the store. `rawOptions` are the launch options as passed to reactLobbyApp.
 */
export const startLobbyConfigDiagnostics = (rawOptions: Partial<ISettings>): void => {
  if (started) return;
  started = true;

  checkSsiPresence();
  checkCoreHosts();
  checkCampaignHosts(rawOptions);
  checkQaHostsInRelease();
  checkCampaignOnly();

  if (useAppStore.getState().settingsRequestDone) {
    runSettingsLandedChecks();
  }

  useAppStore.subscribe((state, prev) => {
    if (state.settingsRequestDone && !prev.settingsRequestDone) {
      runSettingsLandedChecks();
    }
    if (state.settings.campaignOnly?.type !== prev.settings.campaignOnly?.type) {
      checkCampaignOnly();
    }
  });
};
